// src/App.js

import React, { useState, useEffect, useRef } from "react";
import WorldCoverageMap from "./components/WorldCoverageMap";
import ChatWidget from "./components/ChatWidget.jsx";
import { Helmet, HelmetProvider } from "react-helmet-async";


// UTM keys to capture
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_region", "utm_owner"];

/* -------------------------
   Countries by Region (single source of truth)
--------------------------*/
const COUNTRIES_BY_REGION = {
  "West and Central Africa": [
    { name: "Benin", code: "BJ" },
    { name: "Burkina Faso", code: "BF" },
    { name: "Cabo Verde", code: "CV" },
    { name: "Côte d’Ivoire", code: "CI" },
    { name: "Gambia", code: "GM" },
    { name: "Ghana", code: "GH" },
    { name: "Guinea", code: "GN" },
    { name: "Guinea-Bissau", code: "GW" },
    { name: "Liberia", code: "LR" },
    { name: "Mali", code: "ML" },
    { name: "Mauritania", code: "MR" },
    { name: "Niger", code: "NE" },
    { name: "Nigeria", code: "NG" },
    { name: "Senegal", code: "SN" },
    { name: "Sierra Leone", code: "SL" },
    { name: "Togo", code: "TG" },
    { name: "Cameroon", code: "CM" },
    { name: "Central African Republic", code: "CF" },
    { name: "Congo (DRC)", code: "CD" },
    { name: "Republic of the Congo", code: "CG" },
    { name: "Gabon", code: "GA" },
    { name: "Equatorial Guinea", code: "GQ" }
  ],

  "East and Southern Africa": [
    { name: "Angola", code: "AO" },
    { name: "Botswana", code: "BW" },
    { name: "Burundi", code: "BI" },
    { name: "Comoros", code: "KM" },
    { name: "Djibouti", code: "DJ" },
    { name: "Eswatini", code: "SZ" },
    { name: "Ethiopia", code: "ET" },
    { name: "Kenya", code: "KE" },
    { name: "Lesotho", code: "LS" },
    { name: "Madagascar", code: "MG" },
    { name: "Malawi", code: "MW" },
    { name: "Mauritius", code: "MU" },
    { name: "Mozambique", code: "MZ" },
    { name: "Namibia", code: "NA" },
    { name: "Rwanda", code: "RW" },
    { name: "Somalia", code: "SO" },
    { name: "South Africa", code: "ZA" },
    { name: "South Sudan", code: "SS" },
    { name: "Sudan", code: "SD" },
    { name: "Tanzania", code: "TZ" },
    { name: "Uganda", code: "UG" },
    { name: "Zambia", code: "ZM" },
    { name: "Zimbabwe", code: "ZW" }
  ],

  "Middle East & North Africa": [
    { name: "Algeria", code: "DZ" },
    { name: "Egypt", code: "EG" },
    { name: "Iraq", code: "IQ" },
    { name: "Jordan", code: "JO" },
    { name: "Lebanon", code: "LB" },
    { name: "Morocco", code: "MA" },
    { name: "Palestine (West Bank and Gaza)", code: "PS" },
    { name: "Syria", code: "SY" },
    { name: "Tunisia", code: "TN" }
  ],

  "Southeast Asia & the Pacific": [
    { name: "Cambodia", code: "KH" },
    { name: "Indonesia", code: "ID" },
    { name: "Laos", code: "LA" },
    { name: "Papua New Guinea", code: "PG" },
    { name: "Philippines", code: "PH" },
    { name: "Thailand", code: "TH" },
    { name: "Timor-Leste", code: "TL" },
    { name: "Vietnam", code: "VN" },
    { name: "Fiji", code: "FJ" },
    { name: "Kiribati", code: "KI" },
    { name: "Marshall Islands", code: "MH" },
    { name: "Micronesia", code: "FM" },
    { name: "Nauru", code: "NR" },
    { name: "Palau", code: "PW" },
    { name: "Solomon Islands", code: "SB" },
    { name: "Tuvalu", code: "TV" }
  ],

  "South Asia": [
    { name: "Bangladesh", code: "BD" },
    { name: "India", code: "IN" },
    { name: "Nepal", code: "NP" },
    { name: "Pakistan", code: "PK" },
    { name: "Sri Lanka", code: "LK" }
  ],

  "Central Asia": [
    { name: "Kazakhstan", code: "KZ" },
    { name: "Kyrgyzstan", code: "KG" },
    { name: "Tajikistan", code: "TJ" },
    { name: "Uzbekistan", code: "UZ" }
    // (Optional) If you want to include Mongolia: { name: "Mongolia", code: "MN" }
  ],

  "Eastern Europe & Eurasia": [
    { name: "Albania", code: "AL" },
    { name: "Armenia", code: "AM" },
    { name: "Bosnia and Herzegovina", code: "BA" },
    { name: "Georgia", code: "GE" },
    { name: "Kosovo", code: "XK" },
    { name: "Moldova", code: "MD" },
    { name: "Serbia", code: "RS" },
    { name: "Ukraine", code: "UA" },
    { name: "North Macedonia", code: "MK" }
  ],

  "Latin America & the Caribbean": [
    { name: "Barbados", code: "BB" },
    { name: "Colombia", code: "CO" },
    { name: "Dominican Republic", code: "DO" },
    { name: "Ecuador", code: "EC" },
    { name: "El Salvador", code: "SV" },
    { name: "Guatemala", code: "GT" },
    { name: "Guyana", code: "GY" },
    { name: "Haiti", code: "HT" },
    { name: "Honduras", code: "HN" },
    { name: "Jamaica", code: "JM" },
    { name: "Mexico", code: "MX" },
    { name: "Nicaragua", code: "NI" },
    { name: "Paraguay", code: "PY" },
    { name: "Peru", code: "PE" }
  ]
};

// helpers
const findRegionByCode = (code) => {
  for (const [region, arr] of Object.entries(COUNTRIES_BY_REGION)) {
    if (arr.some((c) => c.code === code)) return region;
  }
  return "";
};
const findCountryNameByCode = (code) => {
  for (const arr of Object.values(COUNTRIES_BY_REGION)) {
    const hit = arr.find((c) => c.code === code);
    if (hit) return hit.name;
  }
  return "";
};

/* -------------------------
   Soft card tones
--------------------------*/
const toneClass = (variant = "slate") => {
  const map = {
    indigo: "ring-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50",
    rose: "ring-rose-200 bg-gradient-to-br from-rose-50 to-pink-50",
    emerald: "ring-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50",
    amber: "ring-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50",
    sky: "ring-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50",
    violet: "ring-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50",
    slate: "ring-slate-200 bg-white"
  };
  return map[variant] || map.slate;
};

/* -------------------------
   Dual-Hat VP + RMC Card (no chips; bigger photo)
--------------------------*/
const DualHatCard = ({
  name,
  headshot,
  vpTitle,
  vpScope = "Global",
  rmcTitle = "Regional Managing Partner",
  rmcRegion,
  onOpenBio,
  variant = "slate"
}) => (
  <article className={`relative rounded-2xl ${toneClass(variant)} ring-1 shadow-md hover:shadow-lg transition-all p-6 hover:-translate-y-0.5 h-full`}>
    <span className="absolute -top-3 left-4 inline-flex items-center rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white shadow-sm">Dual-Hat</span>

    <div className="grid grid-cols-12 gap-6 items-center">
      {/* Text */}
      <div className="col-span-12 md:col-span-8">
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold rounded-full bg-white/80 text-slate-900 px-2 py-1 ring-1 ring-black/10">VP</span>
          <span className="text-xs font-semibold rounded-full bg-white/80 text-slate-900 px-2 py-1 ring-1 ring-black/10">RMP</span>
        </div>
        <h4 className="mt-2 text-xl font-semibold text-slate-900">{name}</h4>

        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl bg-white/85 ring-1 ring-white/60 p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-600">VP Role</p>
            <p className="mt-1 text-sm font-medium text-slate-900">{vpTitle}</p>
            <p className="text-xs text-slate-700 mt-1">Scope: {vpScope}</p>
          </div>
          <div className="rounded-xl bg-white/85 ring-1 ring-white/60 p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-600">RMP Role</p>
            <p className="mt-1 text-sm font-medium text-slate-900">{rmcTitle}</p>
            <p className="text-xs text-slate-700 mt-1">Region: {rmcRegion}</p>
          </div>
        </div>

        <button onClick={onOpenBio} className="mt-5 text-sm font-medium text-blue-700 hover:text-blue-800 underline underline-offset-4">
          View bio
        </button>
      </div>

      {/* Big photo */}
      <div className="col-span-12 md:col-span-4 flex md:justify-end">
        <img
          src={headshot}
          alt={`${name} headshot`}
          className="h-40 w-40 md:h-44 md:w-44 rounded-2xl object-cover ring-2 ring-white/80 shadow-sm"
          onError={(e) => (e.currentTarget.src = "/profiles/avatar_01.png")}
          loading="lazy"
        />
      </div>
    </div>
  </article>
);

/* -------------------------
   RMC Card (no chips; bigger photo)
--------------------------*/
const RmcCard = ({ profile, onOpen }) => {
  const { name, title, region, headshot, variant = "slate" } = profile;
  return (
    <article className={`group rounded-2xl ${toneClass(variant)} ring-1 shadow-sm hover:shadow-md transition-all p-6 hover:-translate-y-0.5 h-full`}>
      <div className="grid grid-cols-12 gap-4 items-center">
        <div className="col-span-12 lg:col-span-7">
          <h4 className="text-lg font-semibold text-slate-900">{name}</h4>
          <p className="text-sm text-slate-700">{title}</p>
          <p className="text-xs text-slate-600 mt-0.5">{region}</p>

          <button
            onClick={onOpen}
            className="mt-5 text-sm font-medium text-blue-700 hover:text-blue-800 underline underline-offset-4"
            aria-label={`Read bio for ${name}`}
          >
            View bio
          </button>
        </div>

        <div className="col-span-12 lg:col-span-5 flex lg:justify-end">
          <img
            src={headshot}
            alt={`${name} headshot`}
            className="h-32 w-32 lg:h-36 lg:w-36 rounded-2xl object-cover ring-2 ring-white/80 shadow-sm"
            onError={(e) => (e.currentTarget.src = "/profiles/avatar_01.png")}
            loading="lazy"
          />
        </div>
      </div>
    </article>
  );
};

/* -------------------------
   RMC Grid Profiles (non-VP)
--------------------------*/
const RMC_PROFILES = [
  {
    name: "George Khisa",
    title: "Regional Managing Partner",
    region: "East & Southern Africa",
    headshot: "/profiles/avatar_02.png",
    bio: "Leads multi-country initiatives across East and Southern Africa, combining policy reform with practical delivery in tough environments. Based in Nairobi, George is an international leader with 20 years of experience across Sub-Saharan Africa. He has advanced institutional strengthening through multimillion-dollar partnerships and brings deep expertise in policy reform and regional program delivery.",
    variant: "indigo"
  },
  {
    name: "Asima Rehman",
    title: "Regional Managing Partner",
    region: "South Asia",
    headshot: "/profiles/avatar_07.png",
    bio: "Asima drives high-quality delivery across South Asia, combining deep public-sector expertise with agile, compliant operations and rapid mobilization. With 20 years of experience leading international programs and financing, she has overseen more than $1B in cross-sector initiatives. Her work spans policy, strategy, and innovative financing, with a track record of structuring high-value partnerships that unlock private investment and drive economic growth.",
    variant: "indigo"
  },
  {
    name: "David Tsiklauri",
    title: "Regional Managing Partner",
    region: "Central Asia & Eastern Europe / Eurasia",
    headshot: "/profiles/avatar_04.png",
    bio: "David serves as Regional Managing Partner for Central Asia, Eastern Europe, and Eurasia. With more than 20 years of experience in economic development, investment policy, and trade facilitation, he has advised governments and international investors on strategies to unlock growth in complex markets. David has led multi-million-dollar infrastructure and reform programs across the Caucasus and Central Asia, with particular expertise in improving competitiveness, advancing regional connectivity, and enabling private sector expansion. His work bridges policy and practice—helping organizations navigate regulatory landscapes, identify investment opportunities, and build partnerships that deliver results.",
    variant: "indigo"
  },
  {
    name: "Mir Tillah",
    title: "Regional Managing Partner",
    region: "Southeast Asia & Pacific",
    headshot: "/profiles/avatar_05.png",
    bio: "Operator-turned-advisor with deep networks across SEA & the Pacific; stands up compliant ops fast. Mir is an expert in workforce development, humanitarian assistance, urban and economic growth. He has a robust record of fostering strategic partnerships at the highest levels.",
    variant: "indigo"
  },
  {
    name: "Jose Zarzar",
    title: "Regional Managing Partner",
    region: "Latin America & Caribbean",
    headshot: "/profiles/avatar_06.png",
    bio: "Builds credible delivery through strong local networks, rigorous MEL/TPM, and effective access facilitation across LAC. Jose is a procurement expert and strategic advisor with 15+ years’ experience managing high-value development portfolios in South America. He specializes in innovative procurement solutions, contract negotiations, and cross-cultural leadership.",
    variant: "indigo"
  }
];
const FeaturedConsultantCard = ({ profile, onOpen }) => {
  const { name, headline, region, country, headshot, rateBand, variant = "slate" } = profile;

  return (
    <article className={`rounded-2xl ${toneClass(variant)} ring-1 shadow-sm hover:shadow-md transition-all p-6 hover:-translate-y-0.5 h-full`}>
      <div className="flex items-center gap-4">
        <img
          src={headshot}
          alt={name}
          className="h-20 w-20 rounded-2xl object-cover ring-2 ring-white/80 shadow-sm"
          onError={(e) => (e.currentTarget.src = "/profiles/avatar_01.png")}
          loading="lazy"
        />
        <div>
          <h4 className="text-lg font-semibold text-slate-900">{name}</h4>
          <p className="text-sm text-slate-700">{headline}</p>
          <p className="text-xs text-slate-600 mt-1">
            {country} · {region}
          </p>
        </div>
      </div>

      <p className="mt-4 text-sm text-slate-700">{rateBand}</p>

      <button
        onClick={onOpen}
        className="mt-4 text-sm font-medium text-blue-700 hover:text-blue-800 underline underline-offset-4"
      >
        View profile
      </button>
    </article>
  );
};

/* -------------------------
   Region Explorer (popover; not a dropdown)
--------------------------*/
const RegionExplorer = ({ data }) => {
  const [activeRegion, setActiveRegion] = React.useState(null);

  // Close popover on ESC
  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActiveRegion(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const regions = Object.keys(data);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Region pills */}
      <div className="flex flex-wrap justify-center gap-3">
        {regions.map((region) => {
          const isOpen = activeRegion === region;
          return (
            <button
              key={region}
              type="button"
              onClick={() => setActiveRegion(isOpen ? null : region)}
              onMouseEnter={() => setActiveRegion(region)}
              onFocus={() => setActiveRegion(region)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition
                ring-1 ring-slate-300 bg-white hover:bg-slate-50
                ${isOpen ? "shadow-md ring-slate-400" : ""}`}
              aria-expanded={isOpen ? "true" : "false"}
              aria-controls={`region-popover-${region}`}
            >
              {region}
            </button>
          );
        })}
      </div>

      {/* Popover */}
      {activeRegion && (
        <div
          id={`region-popover-${activeRegion}`}
          className="relative mt-6"
          onMouseLeave={() => setActiveRegion(null)}
        >
          <div className="mx-auto w-full max-w-4xl rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h5 className="text-base md:text-lg font-semibold text-slate-900">
                {activeRegion}
              </h5>
              <button
                className="text-slate-500 hover:text-slate-800"
                onClick={() => setActiveRegion(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="flex flex-wrap gap-2 justify-start">
              {data[activeRegion].map(({ name }, i) => (
                <span
                  key={i}
                  className="px-3 py-1 rounded-full text-sm bg-slate-100 text-slate-800 ring-1 ring-slate-200"
                >
                  {name}
                </span>
              ))}
            </div>

            <p className="mt-3 text-xs text-slate-500">
              Hover a different region or tap a pill to switch.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
const PARTNERS = [
  {
    
    logo: "/partners/syria-talent-advisory.png"
  },
  {
  
    logo: "/partners/with-both-and.png"
  },
  {
 
    logo: "/partners/kcl-global-advisors.png"
  }
];

function App() {
  const testimonials = [
    { quote: "Venturly Global connected us with top-tier local experts...", name: "Paul A.", role: "Founder & CEO, Go Inc." },
    { quote: "Their consultants understood the terrain better...", name: "Lyndsey M.", role: "Founder & CEO, Tommowmetrics" },
    { quote: "Flexible, fast, and deeply local...", name: "Leila H.", role: "Operations Lead, Social Enterprise" }
  ];

  const [index, setIndex] = useState(0);
  const [selectedRmc, setSelectedRmc] = useState(null);
const [selectedConsultant, setSelectedConsultant] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
const [showAllServices, setShowAllServices] = useState(false);
  const [status, setStatus] = useState("");

  const formRef = useRef(null);
  const aboutRef = useRef(null);
  const featuredConsultantsRef = useRef(null);
  const teamRef = useRef(null);
  const whereRef = useRef(null);
  const servicesRef = useRef(null);
  const engagementRef = useRef(null);
  // Smooth scroll with offset (prevents sticky nav covering the target)
  const scrollToRef = (ref, offset = 96) => {
    if (!ref?.current) return;
    const top =
      ref.current.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: "smooth" });
  };

  useEffect(() => {
    const interval = setInterval(() => setIndex((p) => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    UTM_KEYS.forEach((k) => {
      const v = q.get(k);
      if (v) sessionStorage.setItem(k, v);
    });
  }, []);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const APPS_SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbyou6Xhh8925G_-_GbmiQnQd-m6MiJ2BPfoh3-_wtSpy5t7Sd3l_Z_8EboN5-Ve1u4S/exec";
// Consultant Intake Form (Google Form)
const CONSULTANT_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSf6Ij8n3IuzH-AskRkPEr-wtUUBYpiJj8Jttco2ObXqUGEZTg/viewform?usp=header";

// Build tracked URL with UTM + referrer + path
const buildConsultantUrl = () => {
  if (typeof window === "undefined") return CONSULTANT_FORM_URL;
  const url = new URL(CONSULTANT_FORM_URL);

  // preserve existing params (like usp=header) and add UTM if present
  const q = new URLSearchParams(window.location.search);
  UTM_KEYS.forEach((k) => {
    const v = q.get(k) || sessionStorage.getItem(k);
    if (v) url.searchParams.set(k, v);
  });

  url.searchParams.set("referrer", document.referrer || "");
  url.searchParams.set("path", window.location.pathname || "/");
  return url.toString();
};

  // Smooth-scroll handler for anchor nav (keeps links crawlable)
  const smoothScroll = (e, ref) => {
    if (!ref?.current) return;
    e.preventDefault();
    ref.current.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <HelmetProvider>
      <div className="bg-[#f4f4f5] text-black font-sans min-h-screen relative">
        <Helmet>
          <title>Venturly Global | Access the World’s Most Trusted Local Experts in Frontier and Emerging Markets</title>
          <meta
            name="description"
            content="Venturly Global connects organizations with 2,000+ vetted, in-country consultants across Africa, Asia, Latin America, Eastern Europe & the Middle East — trusted local expertise for market entry, risk reduction, and growth. Vetted Local Consultants in Emerging Markets" />
          <meta property="og:description" content="Vetted, in-country experts for market entry and growth across frontier and emerging markets." />
          <meta property="og:url" content="https://venturlyglobal.com/" />
          <meta property="og:type" content="website" />
          <meta property="og:image" content="https://venturlyglobal.com/assets/og_venturly.jpg" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Venturly Global | Access the World’s Most Trusted Local Experts in Frontier and Emerging Markets" />
          <meta name="twitter:description" content="Trusted local consultants across Africa, Asia, LAC, E. Europe & MENA." />
          <meta name="twitter:image" content="https://venturlyglobal.com/assets/og_venturly.jpg" />

          {/* Organization & Website JSON-LD */}
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Venturly Global",
            "url": "https://venturlyglobal.com/",
            "logo": "https://venturlyglobal.com/assets/venturly_logo_transparent.png",
            "sameAs": [
              "https://www.linkedin.com/company/venturly-global"
            ],
            "description": "Vetted, in-country consultants for market entry and growth across frontier and emerging markets."
          })}</script>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": "https://venturlyglobal.com/",
            "name": "Venturly Global",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://venturlyglobal.com/?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}</script>
        </Helmet>

        {/* NAVIGATION */}
        <nav className="flex justify-between items-center px-8 md:px-12 py-4 bg-white shadow-sm sticky top-0 z-50">
          <img
            src="/assets/venturly_logo_transparent.png"
            alt="Venturly Global"
            className="h-12 md:h-16 w-auto"
            loading="eager"
            decoding="async"
          />
          <div className="space-x-6 text-sm md:text-base flex items-center">
            <a href="#intake-form" onClick={(e) => smoothScroll(e, formRef)}>My Project</a>
            <a href="mailto:info@venturlyglobal.com">Contact</a>
            <a href="#about-us" onClick={(e) => smoothScroll(e, aboutRef)}>About</a>
            <a href="#leadership" onClick={(e) => smoothScroll(e, teamRef)}>Leadership</a>
            <a href="#where-we-operate" onClick={(e) => smoothScroll(e, whereRef)}>Where We Operate</a>
            <a href="#what-we-offer" onClick={(e) => smoothScroll(e, servicesRef)}>What We Offer</a>
            <a href="#how-to-engage" onClick={(e) => smoothScroll(e, engagementRef)}>How to Engage</a>
            <a
  href={buildConsultantUrl()}
  target="_blank"
  rel="noopener noreferrer"
  className="ml-2 inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800"
>
  Join as Consultant
</a>

          </div>
        </nav>

        {/* HERO */}
        <header className="relative isolate overflow-hidden bg-[#e5f0ff]" aria-label="Venturly Global hero">
          <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:flex lg:items-center lg:gap-12 lg:px-8">
            <div className="max-w-2xl">
              <div className="mb-8">
                <img
                  src="/assets/venturly_logo_transparent.png"
                  alt="Venturly Global — vetted consultants for emerging markets"
                  className="h-28 md:h-40 lg:h-48 w-auto drop-shadow-md"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  width="512"
                  height="256"
                />
                <span className="sr-only">Venturly Global</span>
              </div>

              <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">Access the World’s Most Trusted Local Experts in Frontier and Emerging Markets</h1>

              <p className="mt-4 text-lg text-slate-700 sm:text-xl" data-ab-slot="hero-tagline">
                 Venturly Global connects you with rigorously vetted in-country experts across Africa, Asia, Latin America, Eastern Europe & Eurasia, and the Middle East.
              </p>

              <p className="mt-3 text-slate-600">A network of 2,000+ vetted consultants supporting market entry, risk reduction, and growth across frontier and emerging markets.</p>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth" })}
                  className="inline-flex items-center justify-center rounded-2xl px-6 py-3 text-base font-medium text-white bg-slate-900 hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                >
                  Build Your Project
                </button>
                <button
                  onClick={() => scrollToRef(featuredConsultantsRef, 96)}
                  className="inline-flex items-center justify-center rounded-2xl px-6 py-3 text-base font-medium text-slate-900 bg-white ring-1 ring-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                >
                  Hire A Consultant
                </button>
                
              </div>

              <p className="mt-3 text-sm text-slate-500">Trusted. Tested. Ready to deliver.</p>
            </div>

            <div className="mt-10 lg:mt-0 lg:flex-1">
              {/* PASS THE COUNTRY LIST INTO THE MAP */}
              <WorldCoverageMap countriesByRegion={COUNTRIES_BY_REGION} />
            </div>
          </div>
        </header>

        <main>
          {/* ABOUT US */}
          <section ref={aboutRef} id="about-us" className="bg-white py-16">
            <div className="max-w-5xl mx-auto px-6 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">About Us</h2>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
                Venturly Global connects organizations with trusted, in-country experts across frontier and emerging markets. Our consultants are proven operators, advisors, and technical specialists with deep local networks and a track record of delivery in complex environments.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
                Clients engage Venturly Global to work with individual experts for targeted needs or to assemble tailored, multi-disciplinary teams for more complex initiatives, from market entry to large-scale execution. 
      
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                Our mission is simple: unlock opportunities, reduce risk, and help global organizations succeed through proven local knowledge.
              </p>
            </div>
          </section>

          {/* WHERE WE OPERATE (streamlined regions with hover/click popover) */}
          <section ref={whereRef} id="where-we-operate" className="px-6 py-16 text-center bg-[#e5f0ff]">
            <h3 className="text-2xl md:text-3xl font-bold mb-3 text-slate-900">Where We Operate</h3>
            <p className="text-lg max-w-3xl mx-auto mb-8 text-slate-800">
              Venturly Global connects you to vetted, well-connected experts in more than 100 countries in these regions.
            </p>

            <RegionExplorer data={COUNTRIES_BY_REGION} />
          </section>
          
{/* BUILT FOR BOLD CLIENTS */}
{(() => {
  // --- data (ASCII-only strings, no smart quotes) ---
  const INDUSTRIES = [
    { key: "pe", label: "Private Equity & Investors", value: "De-risk market entry and validate opportunities with local diligence.", href: "#industry-private-equity" },
    { key: "startups", label: "Startups & Growth-Stage Firms", value: "Stand up operations fast with vetted local teams and compliant workflows.", href: "#industry-startups" },
    { key: "gov", label: "Government & Public Programs", value: "Deliver policy and services through credible local delivery partners.", href: "#industry-government" },
    { key: "media", label: "Media & Influencers", value: "Source verified voices, fixers, and context with speed and discretion.", href: "#industry-media" },
    { key: "universities", label: "Universities & Research Institutions", value: "Run field research precisely, including permits, sampling, and quality control.", href: "#industry-universities" },
    { key: "thinktanks", label: "Think Tanks & Policy Labs", value: "Translate insights into local pilots and measurable outcomes.", href: "#industry-thinktanks" },
    { key: "bd", label: "Business Development & Proposal Support", value: "Stand up proposal teams fast: capture, recruiting, vetting, pricing, and compliance.", href: "#industry-bd" },
    { key: "ngos", label: "NGOs & Donors", value: "Deliver high-impact programs with trusted local expertise and delivery discipline.", href: "#industry-ngos" },
    { key: "advocacy", label: "Advocates & Coalitions", value: "Build coalitions and engagement strategies with credible in-country networks.", href: "#industry-advocacy" }
  ];

  const DIFFERENTIATORS = [
    { title: "Vetted, Not Open", desc: "No random profiles. Every consultant is handpicked and verified through referrals, credentials, and track record." },
    { title: "Built for High-Stakes Work", desc: "Designed for complex environments where trust, speed, and local nuance determine outcomes." },
    { title: "Emerging Markets Focus", desc: "Deep coverage across Africa, Asia, Latin America, Eastern Europe and Eurasia, and the Middle East." },
    { title: "Flexible Engagement", desc: "Hire an individual specialist quickly or assemble a vetted, multi-disciplinary team for larger scopes." }
  ];

  const TOP_SERVICES = [
    { title: "Market Entry & Stakeholder Engagement", items: ["Government and business introductions", "Local ecosystem mapping", "Community relations and buy-in"] },
    { title: "Regulatory Navigation & Business Setup", items: ["Entity registration and licensing", "Tax and compliance advisory", "Banking, legal, and HR setup"] },
    { title: "Operations, Logistics & Procurement", items: ["Office and warehouse setup", "Fleet and transport management", "Local vendor sourcing and QA"] },
    { title: "Research, MEL & TPM", items: ["Third-party monitoring", "Impact evaluations", "Dashboards and reporting"] },
    { title: "Access & Risk Management", items: ["Local risk assessments", "Secure movement support", "Access facilitation"] },
    { title: "Business Development & Proposal Support", items: ["Capture and partner sourcing", "Proposal teams and role matrices", "Budgeting, pricing, and compliance"] }
  ];

  const ALL_SERVICES = [
    { title: "Strategic Advisory & Policy Reform", items: ["Public sector modernization", "Legal and regulatory reform", "Anti-corruption strategies"] },
    { title: "Education & Workforce Development", items: ["TVET and higher education", "Curriculum design", "Teacher training"] },
    { title: "Health & Social Protection", items: ["Health systems strengthening", "Public health strategy", "MHPSS and GBV response"] },
    { title: "Economic Growth & Jobs", items: ["SME support", "Value chain development", "Local job creation strategies"] },
    { title: "Infrastructure & Energy", items: ["Power sector reform", "Transport and mobility planning", "Climate-resilient cities"] },
    { title: "Climate & Environment", items: ["Disaster risk reduction", "WASH systems", "Natural resource management"] },
    { title: "Justice & Rule of Law", items: ["Access to justice", "Police and court reform", "Transitional justice and reconciliation"] },
    { title: "Research, MEL & TPM", items: ["Third-party monitoring", "Impact evaluations", "Data visualization and dashboards"] },
    { title: "Humanitarian Response & Stabilization", items: ["Emergency assessments", "Access coordination", "Post-conflict recovery"] },
    { title: "Regulatory Navigation & Business Setup", items: ["Entity registration and licensing", "Tax and compliance advisory", "Banking, legal, and HR setup"] },
    { title: "Operations, Logistics & Procurement", items: ["Office and warehouse setup", "Fleet and transport management", "Local vendor sourcing and QA"] },
    {
      title: "Business Development & Proposal Support",
      items: [
        "Capture and partner sourcing (teaming, LOIs, MOUs)",
        "Targeted recruiting and proposal teams",
        "CV tailoring and role matrices",
        "Budgeting, pricing, and rate bands",
        "Compliance and due diligence",
        "Proposal writing and submission QA/QC"
      ]
    },
    { title: "Stakeholder Engagement & Market Entry", items: ["Government and business introductions", "Community relations and buy-in", "Local ecosystem mapping"] },
    {
      title: "Media, Research & Production Logistics",
      items: [
        "Location scouting and access facilitation",
        "Fixer and translator services",
        "Permit and clearance navigation",
        "Crew logistics and security coordination"
      ]
    },
    { title: "Access & Risk Management", items: ["Local risk assessments", "Secure movement and logistics", "Community and government access facilitation"] }
  ];


  return (
    <>
      {/* BUILT FOR BOLD CLIENTS */}
      <section className="bg-[#fefefe] py-14 px-6 text-black" id="built-for-bold-clients">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold text-center">Built for Bold Clients</h3>
          <p className="text-base md:text-lg text-slate-700 text-center mt-3 max-w-3xl mx-auto">
            Venturly Global supports organizations working in complex markets where local context is decisive.
            Engage us to hire a single specialist quickly or assemble a vetted, multi-disciplinary team for larger scopes.
          </p>

          {/* INDUSTRIES */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INDUSTRIES.map((item) => (
              <a
                key={item.key}
                href={item.href}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-1 inline-block h-2.5 w-2.5 rounded-full bg-blue-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <h4 className="font-semibold text-slate-900">{item.label}</h4>
                    <p className="text-sm text-slate-600 mt-1">{item.value}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>

          <p className="text-center text-slate-700 mt-6">...and high-impact teams like yours.</p>
        </div>
      </section>

      {/* WHY VENTURLY IS DIFFERENT (tighter layout) */}
      <section className="bg-[#fefefe] py-12 px-6 text-center text-black">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold mb-3">Why Venturly Is Different</h3>
          <p className="text-base md:text-lg max-w-3xl mx-auto mb-8 text-slate-700">
            A curated network built for trust, speed, and delivery in frontier and emerging markets.
          </p>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 text-left">
            {DIFFERENTIATORS.map((d) => (
              <div key={d.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h4 className="text-sm font-semibold text-slate-900">{d.title}</h4>
                <p className="mt-2 text-sm text-slate-600">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES (compact by default with a toggle) */}
      <section ref={servicesRef} id="what-we-offer" className="bg-white py-12 px-6 text-center text-black">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold mb-3">What We Offer</h3>
          <p className="text-base md:text-lg max-w-4xl mx-auto mb-8 text-slate-700">
            Practical, on-the-ground capability across market entry, operations, policy, access, and risk.
          </p>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 text-left">
            {(showAllServices ? ALL_SERVICES : TOP_SERVICES).map(({ title, items }) => (
              <div key={title} className="bg-[#f4f4f5] p-6 rounded-xl shadow-md">
                <h4 className="text-lg font-semibold mb-2">{title}</h4>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <button
              type="button"
              onClick={() => setShowAllServices((v) => !v)}
              className="inline-flex items-center justify-center rounded-2xl px-5 py-2.5 text-sm font-semibold text-slate-900 bg-white ring-1 ring-slate-300 hover:bg-slate-50"
            >
              {showAllServices ? "Show fewer service areas" : "View all service areas"}
            </button>
          </div>
        </div>
      </section>

      {/* TWO WAYS TO ENGAGE (compressed) */}
      <section ref={engagementRef} id="how-to-engage" className="bg-[#e5f0ff] py-12 px-6 text-black">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold text-center mb-3">How to Engage</h3>
          <p className="text-base md:text-lg text-slate-700 text-center max-w-3xl mx-auto mb-8">
            Two engagement paths, with the same vetted network behind both.
          </p>

          <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto text-left">
            <div className="bg-white rounded-2xl shadow-md p-6 border border-slate-200">
              <h4 className="text-lg font-semibold mb-2">Hire an Individual Expert</h4>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>Single specialist or small support pod</li>
                <li>Short sprint or defined deliverable</li>
                <li>Fast start, minimal overhead</li>
              </ul>
              <p className="text-sm text-slate-600 mt-3">
                Best for contained tasks, rapid analysis, light-touch support, or urgent field needs.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6 border border-slate-200">
              <h4 className="text-lg font-semibold mb-2">Build a Tailored Team</h4>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li>Multi-disciplinary team matched to scope</li>
                <li>Multi-country or higher-risk delivery</li>
                <li>Optional coordination and QA/QC</li>
              </ul>
              <p className="text-sm text-slate-600 mt-3">
                Best for complex scopes where quality, speed, and risk management matter.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
})()}
{/* PARTNERSHIPS */}
<section id="partnerships" className="bg-white py-20 px-6">
  <div className="max-w-6xl mx-auto">
    {/* Header */}
    <div className="text-center mb-12">
      <h3 className="text-2xl md:text-3xl font-bold">
        The Venturly Partner Network
      </h3>
      <p className="mt-4 text-slate-600 max-w-3xl mx-auto">
        Venturly Global’s network includes some of the world’s most respected and
        forward-thinking organizations operating in frontier and emerging markets.
        Together, these partners extend our reach, sharpen our insight, and strengthen
        execution where credibility, context, and results matter most.
      </p>
    </div>

    {/* Partner Logos */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
      {PARTNERS.map((p) => (
        <div
          key={p.name}
          className="
            flex items-center justify-center
            rounded-xl
            border border-slate-200
            bg-white
            px-10 py-8
            shadow-sm
            hover:shadow-md
            transition
          "
        >
          <img
            src={p.logo}
            alt={`${p.name} logo`}
            className="max-h-14 w-auto object-contain"
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
        </div>
      ))}
    </div>
  </div>
</section>


          {/* INTAKE FORM */}
          <section ref={formRef} id="intake-form" className="bg-[#f4f4f5] py-12 px-6 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">Let’s Build Something That Lasts</h3>
            <p className="text-lg mb-6">Tell us where you're headed. We'll connect you with the people who can get you there.</p>

            <div className="max-w-xl mx-auto bg-white shadow-lg rounded-xl p-6 border border-gray-200">
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setStatus("");
                  if (isSubmitting) return;
                  const form = e.target;

                  if (form.website.value) {
                    form.reset();
                    setStatus("Thank you for contacting Venturly Global! We'll be in touch.");
                    return;
                  }

                  const name = form.name.value?.trim();
                  const email = form.email.value?.trim();
                  const description = form.description.value?.trim();
                  const country_code = form.country_code.value?.trim();

                  if (!name) return setStatus("Please enter your name.");
                  if (!email || !validateEmail(email)) return setStatus("Please enter a valid email address.");
                  if (!country_code) return setStatus("Please select a country.");
                  if (!description || description.length < 10) return setStatus("Please add a brief project description (10+ characters).");

                  const region = findRegionByCode(country_code) || "";
                  const country_name = findCountryNameByCode(country_code) || "";

                  const payload = new URLSearchParams({
                    name,
                    email,
                    phone: form.phone.value || "",
                    organization: form.organization.value || "",
                    description: description || "",
                    source: "venturly-site",
                    timestamp: new Date().toISOString(),
                    country_code,
                    country_name,
                    region
                  });

                  payload.append("country", country_name);

                  const q = new URLSearchParams(window.location.search);
                  UTM_KEYS.forEach((k) => {
                    const v = q.get(k) || sessionStorage.getItem(k);
                    if (v) payload.append(k, v);
                  });
                  payload.append("referrer", document.referrer || "");
                  payload.append("path", window.location.pathname || "/");

                  const controller = new AbortController();
                  const timeoutId = setTimeout(() => controller.abort(), 12000);

                  try {
                    setIsSubmitting(true);
                    await fetch(APPS_SCRIPT_URL, {
                      method: "POST",
                      mode: "no-cors",
                      headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
                      body: payload.toString(),
                      signal: controller.signal
                    });
                    setStatus("Thank you for contacting Venturly Global! We'll be in touch.");
                    form.reset();
                  } catch (err) {
                    console.error("Submit error:", err);
                    setStatus("Connection issue—please try again in a moment.");
                  } finally {
                    clearTimeout(timeoutId);
                    setIsSubmitting(false);
                  }
                }}
                className="space-y-4 text-left"
              >
                {/* Honeypot */}
                <div className="hidden" aria-hidden="true">
                  <label>
                    Do not fill this out
                    <input type="text" name="website" tabIndex="-1" autoComplete="off" />
                  </label>
                </div>

                <input type="text" name="name" placeholder="Your Name" required className="w-full border px-4 py-2 rounded-md" />
                <input type="email" name="email" placeholder="Your Email" required className="w-full border px-4 py-2 rounded-md" />
                <input type="text" name="phone" placeholder="Phone Number" className="w-full border px-4 py-2 rounded-md" />
                <input type="text" name="organization" placeholder="Organization" className="w-full border px-4 py-2 rounded-md" />

                {/* Country select driven by COUNTRIES_BY_REGION */}
                <select name="country_code" required className="w-full border px-4 py-2 rounded-md">
                  <option value="">Select Country</option>
                  {Object.entries(COUNTRIES_BY_REGION).map(([region, countries]) => (
                    <optgroup key={region} label={region}>
                      {countries.map(({ name, code }) => (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>

                <textarea name="description" placeholder="Brief Project Description" rows="4" className="w-full border px-4 py-2 rounded-md"></textarea>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-6 py-2 rounded-md transition ${isSubmitting ? "bg-gray-400 cursor-not-allowed text-white" : "bg-black text-white hover:bg-gray-800"}`}
                  aria-busy={isSubmitting ? "true" : "false"}
                >
                  {isSubmitting ? "Submitting…" : "Submit"}
                </button>

                <p className="text-sm text-slate-600 min-h-[1.5rem]" aria-live="polite">
                  {status}
                </p>
              </form>
            </div>
          </section>
        </main>
{/* LEADERSHIP */}
          <section ref={teamRef} id="leadership" className="bg-[#fefefe] py-16 px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-end justify-between gap-4 mb-8">
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold">Venturly Global Leadership</h3>
                  <p className="text-slate-600 mt-2">
                    Our dual-hatted VP leads globally and regionally. With them, our Regional Managing Partners deliver in-country with trusted networks.
                  </p>
                </div>
              </div>

              {/* VP dual-hat row */}
              <div className="grid gap-6 lg:grid-cols-1 mb-10">
                
                <DualHatCard
                  name="Clara McLinden"
                  headshot="/profiles/avatar_03.png"
                  vpTitle="Vice President"
                  vpScope="Global"
                  rmcTitle="Regional Managing Partner"
                  rmcRegion="MENA"
                  onOpenBio={() =>
                    setSelectedRmc({
                      name: "Clara McLinden",
                      title: "VP · RMP, MENA",
                      region: "Global / MENA",
                      headshot: "/profiles/avatar_03.png",
                      bio: "Clara serves as Vice President of Venturly Global and Regional Managing Partner for the MENA region. With over a decade of international development experience, she has directed strategy and communications efforts worldwide, with particular expertise in strengthening partnerships and delivering results across the Middle East and North Africa."
                    })
                  }
                  variant="indigo"
                />
              </div>

              {/* RMC grid */}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {RMC_PROFILES.map((p, i) => (
                  <RmcCard key={i} profile={p} onOpen={() => setSelectedRmc(p)} />
                ))}
              </div>
            </div>
          </section>

          {/* RMC BIO MODAL */}
          {selectedRmc && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
              role="dialog"
              aria-modal="true"
              aria-label={`Bio for ${selectedRmc.name}`}
              onClick={() => setSelectedRmc(null)}
            >
              <div
                className="relative max-w-lg w-full bg-white rounded-2xl shadow-xl p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setSelectedRmc(null)}
                  className="absolute top-3 right-3 text-slate-500 hover:text-slate-800 text-xl font-bold"
                  aria-label="Close bio"
                >
                  ×
                </button>
                <div className="flex items-center gap-4">
                  <img
                    src={selectedRmc.headshot}
                    alt={`${selectedRmc.name} headshot`}
                    className="h-20 w-20 rounded-2xl object-cover ring-2 ring-slate-200"
                    onError={(e) => (e.currentTarget.src = "/profiles/avatar_01.png")}
                    loading="lazy"
                  />
                  <div>
                    <h4 className="text-lg font-semibold">{selectedRmc.name}</h4>
                    <p className="text-sm text-slate-600">{selectedRmc.title}</p>
                    <p className="text-xs text-slate-500">{selectedRmc.region}</p>
                  </div>
                </div>
                <p className="mt-4 text-sm text-slate-700 leading-6">{selectedRmc.bio}</p>
              </div>
            </div>
          )}


        {/* FOOTER */}
        <footer className="text-center text-gray-500 text-sm py-6 px-4">© {new Date().getFullYear()} Venturly Inc. | Built for frontier markets. Powered by local expertise.</footer>

        {/* Chatbot widget */}
        <ChatWidget
          APPS_SCRIPT_URL={APPS_SCRIPT_URL}
          position="right-center"
          autoOpenOnce={true}
          delayMs={3000}
          directory={[
            { name: "Clara McLinden", region: "MENA", headshot: "/profiles/avatar_03.png" },
            { name: "George Khisa", region: "East & Southern Africa", headshot: "/profiles/avatar_02.png" },
            { name: "Asima Rehman", region: "South Asia", headshot: "/profiles/avatar_07.png" },
            { name: "David Tsiklauri", region: "Central Asia & Eastern Europe / Eurasia", headshot: "/profiles/avatar_04.png" },
            { name: "Mir Tillah", region: "Southeast Asia & Pacific", headshot: "/profiles/avatar_05.png" },
            { name: "Jose Zarzar", region: "Latin America & Caribbean", headshot: "/profiles/avatar_06.png" }
          ]}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;
